import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, type User } from "../api";

// AuthContext tracks the "is this browser session logged in" state.
// The backend holds the real session in a HttpOnly cookie — this
// context is just the UI mirror.

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount, ask the server who we are. If the cookie is valid we
  // skip the login screen; if it's 401 we fall back to unauth.
  useEffect(() => {
    (async () => {
      try {
        setUser(await api.me());
      } catch (e) {
        if (!(e instanceof ApiError) || e.status !== 401) {
          // eslint-disable-next-line no-console
          console.warn("me() failed", e);
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const u = await api.login(username, password);
    setUser(u);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
    }
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, login, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth must be used inside AuthProvider");
  return c;
}

// Role order: readonly(0) < operator(1) < admin(2)
const roleOrder: Record<string, number> = { readonly: 0, operator: 1, admin: 2 };

export function useRole() {
  const { user } = useAuth();
  const role = user?.role ?? "readonly";
  const level = roleOrder[role] ?? 0;
  return {
    role,
    isAdmin:    level >= roleOrder.admin,
    isOperator: level >= roleOrder.operator, // true for operator AND admin
    isReadonly: level === roleOrder.readonly,
  };
}
